import type { RequireAtLeastOne } from 'type-fest'
import type { TeadsEventBody, Uetq } from '@velasca/types'
import { $settings } from './globals'

interface EventBody {
  /**
   * The name of the event being tracked.
   */
  eventName: string
  /**
   * Additional information or context about the event being tracked. The specific format and content of the data parameter will depend on the event being tracked and the requirements of the analytics platform being used.
   */
  data: Record<string, unknown>
}

interface UetqEventBody {
  eventName: keyof Uetq['knownEvents']
  data: Partial<Uetq['knownParams']>
}

interface TeadsTrackEvent {
  eventName: TeadsEventBody['conversionType']
  data: Omit<TeadsEventBody, 'conversionType'>
}

interface TrackEventOptions {
  googleAnalytics: EventBody
  bingAds: UetqEventBody
  klaviyo: EventBody
  taboola: EventBody
  teads: TeadsTrackEvent
}

/**
 * This function tracks events using:
 * Google Analytics 4
 * Microsoft Bing ADS (UETQ Pixel)
 * Klaviyo
 * Taboola
 * Teads.
 *
 * Every vendor call is deferred through the `velascaConsent` registry: it
 * runs immediately when the Iubenda purpose is already granted, at grant time
 * when consent arrives later in the same page, never otherwise. Registered as
 * user actions (`event: true`): a stored refusal discards them. Vendor
 * `typeof` checks live inside the callbacks so they are evaluated at fire
 * time, after the gated loaders ran.
 *
 * GA4 parameters reference: https://developers.google.com/analytics/devguides/collection/ga4/reference/events.
 * BingAds (UETQ) parameters reference: https://help.ads.microsoft.com/apex/index/3/en/60123.
 * Klaviyo parameters reference: https://developers.klaviyo.com/en/docs/javascript_api#track-events-and-actions.
 * Teads parameters reference: https://help.teads.com/en/articles/6202162-collect-conversion-event-metadata.
 *
 * @param options - An object that accepts a minimum of one configuration object for an analytics platform.
 */
export const trackEvent = ({
  googleAnalytics,
  bingAds,
  klaviyo,
  taboola,
  teads,
}: RequireAtLeastOne<TrackEventOptions>): void => {
  const consent = window.velascaConsent

  // No consent registry, no tracking (consent-manager.liquid renders it
  // before any bundle runs).
  if (!consent) {
    return
  }

  if (googleAnalytics?.eventName) {
    const { eventName, data } = googleAnalytics

    // Generic gtag hit (no send_to): either grant opens the gate —
    // destinations are only configured under their own granted purpose
    // (GA4 → measurement, Google Ads → marketing), so nothing leaks across.
    consent.onAnyPurpose(
      ['measurement', 'marketing'],
      `trackEvent gtag ${eventName}`,
      () => {
        if (typeof window.gtag !== 'undefined') {
          gtag('event', eventName, data)
        }
      },
      { event: true },
    )
  }

  if (bingAds?.eventName) {
    const { eventName, data } = bingAds

    consent.onPurpose(
      'marketing',
      `trackEvent bing ${eventName}`,
      () => {
        if (typeof window.uetq !== 'undefined') {
          window.uetq.push('event', eventName, data)
        }
      },
      { event: true },
    )
  }

  if (klaviyo?.eventName) {
    const { eventName, data } = klaviyo

    consent.onPurpose(
      'marketing',
      `trackEvent klaviyo ${eventName}`,
      () => {
        if (
          typeof window.klaviyo !== 'undefined' &&
          $settings.klaviyo_enabled
        ) {
          window.klaviyo.push(['track', eventName, data])
        }
      },
      { event: true },
    )
  }

  if (taboola?.eventName) {
    const { eventName, data } = taboola

    consent.onPurpose(
      'marketing',
      `trackEvent taboola ${eventName}`,
      () => {
        if (typeof window._tfa !== 'undefined') {
          window._tfa.push({
            notify: 'event',
            name: eventName,
            id: 1522741,
            ...data,
          })
        }
      },
      { event: true },
    )
  }

  if (teads?.eventName) {
    const { eventName, data } = teads

    consent.onPurpose(
      'marketing',
      `trackEvent teads ${eventName}`,
      () => {
        if (typeof window.teads_e !== 'undefined') {
          window.teads_e.push({
            conversionType: eventName,
            ...data,
          })
        }
      },
      { event: true },
    )
  }
}
